import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import {
  Box, Typography, Button, TextField, FormControlLabel,
  Chip, Collapse, Checkbox, IconButton, Popover, ButtonBase,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepButton, InputAdornment, CircularProgress, Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Note as NoteIconIx, Book as BookIconIx, MessageQuestion as MessageQuestionIconIx,
  DocumentText as DocumentTextIconIx, DocumentText1 as DocumentText1IconIx, NoteText as NoteTextIconIx,
  TableDocument as TableDocumentIconIx, Global as GlobalIconIx,
  MagicStar as MagicStarIconIx, MessageTick as MessageTickIconIx, ShoppingBag as ShoppingBagIconIx,
  CodeCircle as CodeCircleIconIx, Gallery as GalleryIconIx, ProfileAdd as ProfileAddIconIx,
  TickCircle as TickCircleIconIx, Filter as FilterIconIx, EmojiHappy as EmojiHappyIconIx,
} from "iconsax-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { getAuthToken } from "../../../../../utils/getAuthToken";
import { slimScrollbarSx } from "../../../../../utils/scrollbarStyles";
import { getFieldOptionSoftPillSx } from "../../../../../utils/taskTemplateChipStyles";
import {
  INSTRUCTION_FIELDS, SOURCE_TYPE_LABELS, ACTION_TOGGLE_GROUPS, SERVER_DOMAIN,
} from "../constants";
import OfficeHoursSection from "./OfficeHoursSection";
import ResponseFormatSection from "./ResponseFormatSection";
import ContactFilterSection from "./ContactFilterSection";
import ActionConfigPanel from "./ActionConfigPanel";
import ExpandableParagraphField from "./ExpandableParagraphField";
import GenerateMenuPanel from "./GenerateMenuPanel";
import PillSwitch, { pillSwitchLabelSx } from "./PillSwitch";
import { LabeledField, inboxInputSx, SHORT_FIELD_WIDTH, skeletonSx } from "./LabeledField";

// Test Agent panel icon Rahim supplied directly (2026-09-02: "use this icon
// for testing") — a message/chat-bubble glyph, replacing the earlier
// AiFaceIcon reuse. currentColor swapped in for the source's hardcoded
// #ffffff so it follows the icon-box's own color like every other icon here.
function TestAgentIcon({ size = 24 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <g>
        <path d="M3.5 7V17C3.5 20 4.97 22 8.5 22H15.5C18.97 22 20.5 20 20.5 17V15.83C20.5 15.3 20.29 14.79 19.91 14.42L19.08 13.59C18.7 13.21 18.49 12.71 18.49 12.18V10C18.49 9.45 18.94 9 19.49 9C20.04 9 20.49 8.55 20.49 8V7C20.49 4 18.96 2 15.49 2H8.49C4.97 2 3.5 4 3.5 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.98998 21.97L7.97998 19C7.97998 17.89 8.86998 17 9.97998 17H13.99C15.09 17 15.98 17.89 15.99 18.99L16.02 21.98" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13.97 7.7793H7.96997" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

// "Save the agent first" empty-state icon Rahim supplied directly
// (2026-09-02: "use this for the save agent first") — a document/edit
// glyph, distinct from the heading's TestAgentIcon above.
function SaveAgentFirstIcon({ size = 24 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <g>
        <path d="M22 8C22 4 20 2 16 2H8C4 2 2 4 2 8V21C2 21.55 2.45 22 3 22H16C20 22 22 20 22 16V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15.5098 11.7404L16.1598 11.0904C16.1998 11.0504 16.2398 11.0104 16.2798 10.9604C17.0898 10.1004 17.4098 9.09036 16.1598 7.84036C14.8398 6.52036 13.7998 6.95036 12.9098 7.84036L7.71979 13.0304C7.51979 13.2304 7.3298 13.6204 7.2898 13.9004L7.0098 15.8804C6.9098 16.6004 7.40979 17.1004 8.12979 17.0004L10.1098 16.7204C10.3898 16.6804 10.7798 16.4904 10.9798 16.2904L12.9198 14.3504" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.1699 8.58008C12.6099 10.1501 13.8399 11.3901 15.4199 11.8301" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

// Knowledge Sources empty-state icon Rahim supplied directly (2026-09-03) —
// an open-book glyph, sits above "Train your AI Agent". Source SVG
// hardcoded fill="white"/"#ffffff", swapped to currentColor so it follows
// the wrapping Box's own color, same convention as this file's other
// Rahim-supplied icons (TestAgentIcon/SaveAgentFirstIcon above).
function KnowledgeSourceIcon({ size = 24 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <g>
        <path d="M12 22.0795C11.7 22.0795 11.4 22.0095 11.15 21.8695C9.28 20.8495 5.99 19.7695 3.93 19.4995L3.64 19.4595C2.33 19.2995 1.25 18.0695 1.25 16.7395V4.65952C1.25 3.86952 1.56 3.14952 2.13 2.62952C2.7 2.10952 3.44 1.85952 4.22 1.92952C6.42 2.09952 9.74 3.19952 11.62 4.37952L11.86 4.51952C11.93 4.55952 12.08 4.55952 12.14 4.52952L12.3 4.42952C14.18 3.24952 17.5 2.12952 19.71 1.93952C19.73 1.93952 19.81 1.93952 19.83 1.93952C20.56 1.86952 21.31 2.12952 21.87 2.64952C22.44 3.16952 22.75 3.88952 22.75 4.67952V16.7495C22.75 18.0895 21.67 19.3095 20.35 19.4695L20.02 19.5095C17.96 19.7795 14.66 20.8695 12.83 21.8795C12.59 22.0195 12.3 22.0795 12 22.0795ZM3.98 3.41952C3.66 3.41952 3.37 3.52952 3.14 3.73952C2.89 3.96952 2.75 4.29952 2.75 4.65952V16.7395C2.75 17.3295 3.26 17.8995 3.83 17.9795L4.13 18.0195C6.38 18.3195 9.83 19.4495 11.83 20.5395C11.92 20.5795 12.05 20.5895 12.1 20.5695C14.1 19.4595 17.57 18.3195 19.83 18.0195L20.17 17.9795C20.74 17.9095 21.25 17.3295 21.25 16.7395V4.66952C21.25 4.29952 21.11 3.97952 20.86 3.73952C20.6 3.50952 20.27 3.39952 19.9 3.41952C19.88 3.41952 19.8 3.41952 19.78 3.41952C17.87 3.58952 14.79 4.61952 13.11 5.66952L12.95 5.77952C12.4 6.11952 11.62 6.11952 11.09 5.78952L10.85 5.64952C9.14 4.59952 6.06 3.57952 4.1 3.41952C4.06 3.41952 4.02 3.41952 3.98 3.41952Z" fill="currentColor" />
        <path d="M12 21.2402C11.59 21.2402 11.25 20.9002 11.25 20.4902V5.49023C11.25 5.08023 11.59 4.74023 12 4.74023C12.41 4.74023 12.75 5.08023 12.75 5.49023V20.4902C12.75 20.9102 12.41 21.2402 12 21.2402Z" fill="currentColor" />
        <path d="M7.75 9.24023H5.5C5.09 9.24023 4.75 8.90023 4.75 8.49023C4.75 8.08023 5.09 7.74023 5.5 7.74023H7.75C8.16 7.74023 8.5 8.08023 8.5 8.49023C8.5 8.90023 8.16 9.24023 7.75 9.24023Z" fill="currentColor" />
        <path d="M8.5 12.2402H5.5C5.09 12.2402 4.75 11.9002 4.75 11.4902C4.75 11.0802 5.09 10.7402 5.5 10.7402H8.5C8.91 10.7402 9.25 11.0802 9.25 11.4902C9.25 11.9002 8.91 12.2402 8.5 12.2402Z" fill="currentColor" />
      </g>
    </svg>
  );
}

const STEPS = ["Create", "Config", "Actions", "Contact Filter"];

// Per-step icons Rahim supplied directly (2026-09-02: "use these emojis ...
// add this icon after making the circle better"), one per wizard step —
// replacing the plain MUI numeral/checkmark StepIcon. All fills/strokes
// swapped to currentColor so a single `color` prop on the wrapper drives
// the whole glyph (the source SVGs hardcoded #ffffff/white assuming a dark
// circle behind them; StepIconCircle below decides the actual color per
// step state instead).
function CreateStepIcon({ size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <g>
        <path d="M18.0203 4.8604C16.8203 3.6604 15.1703 3.72042 14.3603 5.01042L12.5903 7.81041L18.2603 13.4804L21.0603 11.7104C22.2703 10.9504 22.3403 9.1804 21.2003 8.0504" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11.2901 21.4797L7.02011 21.9797C5.18011 22.1897 3.86011 20.8698 4.08011 19.0398L5.06011 10.7598" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.2502 13.4707L18.4902 17.5907C18.7202 19.8907 17.9202 20.6907 15.7402 20.9507" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.5801 7.81116L10.8301 7.70117" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.28027 20.7799L8.46028 17.5898" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M11 6.5C11 6.91 10.94 7.32001 10.83 7.70001C10.72 8.10001 10.56 8.47001 10.35 8.82001C10.11 9.22001 9.81001 9.58 9.46001 9.88C8.67001 10.58 7.64 11 6.5 11C5.99 11 5.51 10.92 5.06 10.76C4.04 10.42 3.18999 9.72001 2.64999 8.82001C2.23999 8.14001 2 7.34 2 6.5C2 5.08 2.65 3.80999 3.69 2.98999C4.46 2.36999 5.44 2 6.5 2C8.99 2 11 4.01 11 6.5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.52002 8.1803V4.82031" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.15981 6.5H4.7998" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

function ConfigStepIcon({ size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <g>
        <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H15C20.43 1.25 22.75 3.57 22.75 9V15C22.75 20.43 20.43 22.75 15 22.75ZM9 2.75C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V9C21.25 4.39 19.61 2.75 15 2.75H9Z" />
        <path d="M15.5801 19.2496C15.1701 19.2496 14.8301 18.9096 14.8301 18.4996V14.5996C14.8301 14.1896 15.1701 13.8496 15.5801 13.8496C15.9901 13.8496 16.3301 14.1896 16.3301 14.5996V18.4996C16.3301 18.9096 15.9901 19.2496 15.5801 19.2496Z" />
        <path d="M15.5801 8.2C15.1701 8.2 14.8301 7.86 14.8301 7.45V5.5C14.8301 5.09 15.1701 4.75 15.5801 4.75C15.9901 4.75 16.3301 5.09 16.3301 5.5V7.45C16.3301 7.86 15.9901 8.2 15.5801 8.2Z" />
        <path d="M15.58 13.3992C13.73 13.3992 12.23 11.8992 12.23 10.0492C12.23 8.19922 13.73 6.69922 15.58 6.69922C17.43 6.69922 18.93 8.19922 18.93 10.0492C18.93 11.8992 17.42 13.3992 15.58 13.3992ZM15.58 8.19922C14.56 8.19922 13.73 9.02922 13.73 10.0492C13.73 11.0692 14.56 11.8992 15.58 11.8992C16.6 11.8992 17.43 11.0692 17.43 10.0492C17.43 9.02922 16.59 8.19922 15.58 8.19922Z" />
        <path d="M8.41992 19.2508C8.00992 19.2508 7.66992 18.9108 7.66992 18.5008V16.5508C7.66992 16.1408 8.00992 15.8008 8.41992 15.8008C8.82992 15.8008 9.16992 16.1408 9.16992 16.5508V18.5008C9.16992 18.9108 8.83992 19.2508 8.41992 19.2508Z" />
        <path d="M8.41992 10.15C8.00992 10.15 7.66992 9.81 7.66992 9.4V5.5C7.66992 5.09 8.00992 4.75 8.41992 4.75C8.82992 4.75 9.16992 5.09 9.16992 5.5V9.4C9.16992 9.81 8.83992 10.15 8.41992 10.15Z" />
        <path d="M8.42007 17.2996C6.57007 17.2996 5.07007 15.7996 5.07007 13.9496C5.07007 12.0996 6.57007 10.5996 8.42007 10.5996C10.2701 10.5996 11.7701 12.0996 11.7701 13.9496C11.7701 15.7996 10.2701 17.2996 8.42007 17.2996ZM8.42007 12.0996C7.40007 12.0996 6.57007 12.9296 6.57007 13.9496C6.57007 14.9696 7.40007 15.7996 8.42007 15.7996C9.44007 15.7996 10.2701 14.9696 10.2701 13.9496C10.2701 12.9296 9.45007 12.0996 8.42007 12.0996Z" />
      </g>
    </svg>
  );
}

function ActionsStepIcon({ size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <g>
        <path d="M19 8.75C16.93 8.75 15.25 7.07 15.25 5C15.25 2.93 16.93 1.25 19 1.25C21.07 1.25 22.75 2.93 22.75 5C22.75 7.07 21.07 8.75 19 8.75ZM19 2.75C17.76 2.75 16.75 3.76 16.75 5C16.75 6.24 17.76 7.25 19 7.25C20.24 7.25 21.25 6.24 21.25 5C21.25 3.76 20.24 2.75 19 2.75Z" />
        <path d="M12 13.75H7C6.59 13.75 6.25 13.41 6.25 13C6.25 12.59 6.59 12.25 7 12.25H12C12.41 12.25 12.75 12.59 12.75 13C12.75 13.41 12.41 13.75 12 13.75Z" />
        <path d="M16 17.75H7C6.59 17.75 6.25 17.41 6.25 17C6.25 16.59 6.59 16.25 7 16.25H16C16.41 16.25 16.75 16.59 16.75 17C16.75 17.41 16.41 17.75 16 17.75Z" />
        <path d="M15 22.75H9C3.57 22.75 1.25 20.43 1.25 15V9C1.25 3.57 3.57 1.25 9 1.25H14C14.41 1.25 14.75 1.59 14.75 2C14.75 2.41 14.41 2.75 14 2.75H9C4.39 2.75 2.75 4.39 2.75 9V15C2.75 19.61 4.39 21.25 9 21.25H15C19.61 21.25 21.25 19.61 21.25 15V10C21.25 9.59 21.59 9.25 22 9.25C22.41 9.25 22.75 9.59 22.75 10V15C22.75 20.43 20.43 22.75 15 22.75Z" />
      </g>
    </svg>
  );
}

function ContactFilterStepIcon({ size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none">
      <g>
        <path d="M17 21H7C3 21 2 20 2 16V8C2 4 3 3 7 3H17C21 3 22 4 22 8V16C22 20 21 21 17 21Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 8H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 16H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.49994 11.2899C9.49958 11.2899 10.3099 10.4796 10.3099 9.47992C10.3099 8.48029 9.49958 7.66992 8.49994 7.66992C7.50031 7.66992 6.68994 8.48029 6.68994 9.47992C6.68994 10.4796 7.50031 11.2899 8.49994 11.2899Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 16.3298C11.86 14.8798 10.71 13.7398 9.26 13.6098C8.76 13.5598 8.25 13.5598 7.74 13.6098C6.29 13.7498 5.14 14.8798 5 16.3298" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>
    </svg>
  );
}

// Bold (filled) variants Rahim supplied directly (2026-09-02: "i want
// filled one for all the icons i took the svg from the iconsax") — used
// for the active/completed step instead of the outline set above, which
// stays for upcoming steps. No Bold version of ConfigStepIcon was sent, so
// it's reused as-is in both states (it was already a solid-fill glyph).
function BoldCreateStepIcon({ size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <g>
        <path d="M6.5 2C5.44 2 4.46 2.37 3.69 2.99C2.65 3.81 2 5.08 2 6.5C2 7.34 2.24 8.14 2.65 8.82C3.19 9.72 4.04 10.42 5.06 10.76C5.51 10.92 5.99 11 6.5 11C7.64 11 8.67 10.58 9.46 9.88C9.81 9.58 10.11 9.22 10.35 8.82C10.56 8.47 10.72 8.1 10.83 7.7C10.94 7.32 11 6.91 11 6.5C11 4.01 8.99 2 6.5 2ZM8.16 7.25H7.27V8.18C7.27 8.59 6.93 8.93 6.52 8.93C6.11 8.93 5.77 8.6 5.77 8.18V7.25H4.8C4.39 7.25 4.05 6.91 4.05 6.5C4.05 6.09 4.39 5.75 4.8 5.75H5.77V4.82C5.77 4.41 6.11 4.07 6.52 4.07C6.93 4.07 7.27 4.4 7.27 4.82V5.75H8.16C8.57 5.75 8.91 6.09 8.91 6.5C8.91 6.91 8.58 7.25 8.16 7.25Z" />
        <path d="M16.0001 21.2902L6.6401 22.3902C5.9901 22.4702 5.4101 22.3702 4.9201 22.1302C4.4001 21.8702 3.9901 21.4702 3.7301 20.9402C3.4901 20.4502 3.4001 19.8802 3.4701 19.2402L4.3201 12.0902C4.4001 12.1202 4.4801 12.1502 4.5601 12.1702C5.1701 12.3902 5.8201 12.5002 6.5001 12.5002C7.9601 12.5002 9.3701 11.9702 10.4401 11.0202C10.9001 10.6202 11.3101 10.1402 11.6401 9.59016C11.9201 9.12016 12.1301 8.62016 12.2701 8.13016C12.3401 7.87016 12.4001 7.59016 12.4401 7.31016C12.4501 7.26016 12.4501 7.21016 12.4501 7.16016L12.6201 7.17016L18.7001 13.2602L18.9601 17.6802C19.2101 20.1602 18.3501 21.0202 16.0001 21.2902Z" />
        <path d="M22.0001 10.2799L19.9301 11.5899C19.5401 11.8299 19.0401 11.7799 18.7201 11.4499L14.2901 7.0199C13.9701 6.6999 13.9101 6.1999 14.1501 5.8099L15.4601 3.7399C16.2601 2.4799 17.8601 2.4199 19.0501 3.5899L22.1601 6.6999C23.2501 7.8099 23.1801 9.5299 22.0001 10.2799Z" />
      </g>
    </svg>
  );
}

function BoldActionsStepIcon({ size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <g>
        <path d="M19 8C20.6569 8 22 6.65685 22 5C22 3.34315 20.6569 2 19 2C17.3431 2 16 3.34315 16 5C16 6.65685 17.3431 8 19 8Z" />
        <path d="M19.8 9.42C19.78 9.42 19.76 9.43 19.74 9.43C19.64 9.45 19.54 9.46 19.43 9.48C19.01 9.52 18.56 9.5 18.1 9.41C17.98 9.38 17.88 9.36 17.77 9.32C17.44 9.24 17.13 9.11 16.84 8.94C16.72 8.88 16.6 8.8 16.49 8.73C16.01 8.4 15.6 7.99 15.27 7.51C15.2 7.4 15.12 7.28 15.06 7.16C14.89 6.87 14.76 6.56 14.68 6.23C14.64 6.12 14.62 6.02 14.59 5.9C14.5 5.44 14.48 4.99 14.52 4.57C14.54 4.46 14.55 4.36 14.57 4.26C14.57 4.24 14.58 4.22 14.58 4.2C14.7 3.58 14.24 3 13.6 3H7.52C7.38 3 7.24 3.01 7.11 3.02C6.99 3.03 6.88 3.04 6.76 3.06C6.64 3.07 6.52 3.09 6.41 3.11C4 3.46 2.46 4.99 2.11 7.41C2.09 7.52 2.07 7.64 2.06 7.76C2.04 7.88 2.03 7.99 2.02 8.11C2.01 8.24 2 8.38 2 8.52V16.48C2 16.62 2.01 16.76 2.02 16.89C2.03 17.01 2.04 17.12 2.06 17.24C2.07 17.36 2.09 17.48 2.11 17.59C2.46 20.01 4 21.54 6.41 21.89C6.52 21.91 6.64 21.93 6.76 21.94C6.88 21.96 6.99 21.97 7.11 21.98C7.24 21.99 7.38 22 7.52 22H15.48C15.62 22 15.76 21.99 15.89 21.98C16.01 21.97 16.12 21.96 16.24 21.94C16.36 21.93 16.48 21.91 16.59 21.89C19 21.54 20.54 20.01 20.89 17.59C20.91 17.48 20.93 17.36 20.94 17.24C20.96 17.12 20.97 17.01 20.98 16.89C20.99 16.76 21 16.62 21 16.48V10.4C21 9.76 20.42 9.3 19.8 9.42ZM6.75 12.5H11.75C12.16 12.5 12.5 12.84 12.5 13.25C12.5 13.66 12.16 14 11.75 14H6.75C6.34 14 6 13.66 6 13.25C6 12.84 6.34 12.5 6.75 12.5ZM15.75 18H6.75C6.34 18 6 17.66 6 17.25C6 16.84 6.34 16.5 6.75 16.5H15.75C16.16 16.5 16.5 16.84 16.5 17.25C16.5 17.66 16.16 18 15.75 18Z" />
      </g>
    </svg>
  );
}

function BoldContactFilterStepIcon({ size = 20 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <g>
        <path d="M18 3H6C3.79 3 2 4.78 2 6.97V17.03C2 19.22 3.79 21 6 21H18C20.21 21 22 19.22 22 17.03V6.97C22 4.78 20.21 3 18 3ZM8.5 7.17C9.77 7.17 10.81 8.21 10.81 9.48C10.81 10.75 9.77 11.79 8.5 11.79C7.23 11.79 6.19 10.75 6.19 9.48C6.19 8.21 7.23 7.17 8.5 7.17ZM12.37 16.66C12.28 16.76 12.14 16.82 12 16.82H5C4.86 16.82 4.72 16.76 4.63 16.66C4.54 16.56 4.49 16.42 4.5 16.28C4.67 14.6 6.01 13.27 7.69 13.11C8.22 13.06 8.77 13.06 9.3 13.11C10.98 13.27 12.33 14.6 12.49 16.28C12.51 16.42 12.46 16.56 12.37 16.66ZM19 16.75H17C16.59 16.75 16.25 16.41 16.25 16C16.25 15.59 16.59 15.25 17 15.25H19C19.41 15.25 19.75 15.59 19.75 16C19.75 16.41 19.41 16.75 19 16.75ZM19 12.75H15C14.59 12.75 14.25 12.41 14.25 12C14.25 11.59 14.59 11.25 15 11.25H19C19.41 11.25 19.75 11.59 19.75 12C19.75 12.41 19.41 12.75 19 12.75ZM19 8.75H14C13.59 8.75 13.25 8.41 13.25 8C13.25 7.59 13.59 7.25 14 7.25H19C19.41 7.25 19.75 7.59 19.75 8C19.75 8.41 19.41 8.75 19 8.75Z" />
      </g>
    </svg>
  );
}

const STEP_ICON_COMPONENTS = [CreateStepIcon, ConfigStepIcon, ActionsStepIcon, ContactFilterStepIcon];
const STEP_ICON_COMPONENTS_BOLD = [BoldCreateStepIcon, ConfigStepIcon, BoldActionsStepIcon, BoldContactFilterStepIcon];

// Replaces MUI's default numeral/checkmark StepIcon. Circle design (Rahim,
// 2026-09-02: "i dont like the design of the colour and the circle its very
// weird ... make the ui ux better"): solid fill for the active step and
// every completed step behind it, plain bordered/muted circle for steps
// still ahead — no green (that's "Active" status elsewhere on this page),
// no checkmark override eating the step's own icon.
// Color history: purple -> blue (rejected) -> neutral text.primary black
// (too dark) -> mid-grey #5c5c5c (rejected) -> teal/cyan (rejected) -> back
// to purple, this time deliberately: "use purplish type colour globaly
// used for the ai one" — #7c3aed is the literal same accent as the Get
// Started panel / Credits Balance chip elsewhere on this page, reused on
// purpose now instead of avoided for reuse.
// Glyph still swaps to the Bold set when filled (Rahim: "when the field is
// selected use filled icon... i want filled one for all the icons i took
// the svg from the iconsax") — the outline set stays for upcoming steps.
// Lightened from #7c3aed (Rahim, 2026-09-02: "make the purple colour light
// purple for create config etc") — same violet hue, one shade lighter.
const STEP_FILLED_COLOR = "#a78bfa";

// 40->28px (Rahim, 2026-09-03: "make the create config ... smaller and go
// more up so ... we get more view point") — the stepper was one of two
// full rows of pure navigation chrome above any real content; shrinking
// the circles (and the connector re-anchor + label size below) is the
// safe, in-place way to reclaim height without redesigning the stepper's
// actual icon-over-label shape. Nudged back up twice after that (Rahim:
// "make the icon and text a little bigger" then "just a little more
// big") — now 36px, still well under the original 40px.
function WizardStepIcon({ active, completed, icon }) {
  const filled = active || completed;
  const StepGlyph = (filled ? STEP_ICON_COMPONENTS_BOLD : STEP_ICON_COMPONENTS)[icon - 1] || CreateStepIcon;
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        bgcolor: filled ? STEP_FILLED_COLOR : "background.paper",
        border: filled ? "none" : "1.5px solid",
        borderColor: "divider",
        color: filled ? "#fff" : "text.secondary",
        transition: "background-color 0.15s, color 0.15s",
      }}
    >
      <StepGlyph size={19} />
    </Box>
  );
}

// Same neutral button style used everywhere else on this page
// (AgentsTab.jsx's own neutralButtonSx) — this view still had plain
// variant="contained" buttons, which default to primary.main (the brand
// green), a hard "never ever do a green button" violation everywhere else
// on AI Agents (Rahim, 2026-09-02: "for the buttons make it according ot
// our new design"). No filled/contained buttons exist anywhere else on
// this feature — every action here (Save Agent, Next, Add Intent, etc.) is
// this same bordered/neutral treatment now, for consistency.
const neutralButtonSx = {
  borderRadius: "8px",
  backgroundColor: (theme) => theme.palette.background.default,
  borderColor: (theme) => theme.palette.divider,
  "&:hover": {
    backgroundColor: (theme) => theme.palette.action.hover,
    borderColor: (theme) => theme.palette.divider,
  },
  color: (theme) => theme.palette.text.primary,
  textTransform: "none",
  fontWeight: 600,
};

// LabeledField/inboxInputSx/SHORT_FIELD_WIDTH moved to LabeledField.jsx
// (2026-09-03) so ResponseFormatSection.jsx's Selects could reuse the same
// convention without a circular import back through this file — see that
// file for the definitions/history. Imported below.

// Bordered card wrapping one logical group of the Config step (Rahim,
// 2026-09-02: the step read as "cluttery ... individual [fields] ... only
// distinguishing them is a simple [Divider] line which is wrong ui" —
// grouped into real cards instead, one per section, so each group's start/
// end is obvious rather than inferred from a thin line). Same border/
// radius/fill as every other card on this page (Test Agent panel, AgentCard).
// `heading` is optional — OfficeHoursSection/ResponseFormatSection already
// render their own internal heading, so they're wrapped without a second one.
// `description` is an optional one-line explainer under the heading (Rahim,
// 2026-09-02: "add a sub para text for eplain nder the top heading") —
// same treatment ContactFilterSection already uses under its own "Contact
// Filter" heading, just pulled into the shared card instead of one-off.
// `icon` — an iconsax-react component — is rendered in the same bordered-
// square treatment as the Test Agent panel's own heading icon (Rahim,
// 2026-09-02: "add icons for each category from the iconsax").
function SectionCard({ heading, description, icon: Icon, children, sx }) {
  return (
    <Box
      sx={{
        border: "1px solid", borderColor: "divider", borderRadius: "8px",
        // White (background.paper in light mode) read as too stark for
        // this many stacked cards (Rahim, 2026-09-02: "disable the
        // background white colour ... keep the grey type colour for dark
        // mood tho") — light mode now transparent (just the border), dark
        // mode keeps its existing grey background.paper fill.
        bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "transparent"),
        // p:2.5->2, gap:2->1.5 (Rahim, 2026-09-03: "for all meaning message
        // format office hours fallback everything" — internal spacing on
        // every SectionCard, not just the gap between them). This is the
        // one shared component every section goes through (Instructions,
        // Knowledge Sources, Fallback Message, Office Hours, Message
        // Format, Intents), so fixing it here covers all of them.
        p: 2,
        display: "flex", flexDirection: "column", gap: 1.5,
        ...sx,
      }}
    >
      {heading && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          {Icon && (
            // Bumped 24->32 / 14->20 (Rahim, 2026-09-03: "increase the size
            // of the icon" — SectionCard's own heading icon, e.g.
            // Instructions' NoteIconIx) — every SectionCard on this page
            // shares this one component, so the size change applies to all
            // of them uniformly, not just Instructions.
            <Box
              sx={{
                width: 32, height: 32, flexShrink: 0, mt: 0.125,
                borderRadius: "8px",
                border: "1px solid", borderColor: "divider",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "text.primary",
              }}
            >
              <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size="100%" color="currentColor" variant="Linear" />
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>
              {heading}
            </Typography>
            {description && (
              <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
                {description}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      {children}
    </Box>
  );
}

// Same lazy-loaded emoji-mart pattern LifecycleSettings.jsx uses for its
// stage icons (TextBox.jsx / TaskChatPanel.jsx also share it) — reused
// as-is rather than adding a second picker library. Now also reused
// further down for the Test Agent panel's own message input (Rahim,
// 2026-09-04: "instead of adding file button we can give emoji button")
// — same component, module-level, no second declaration needed.
const EmojiPicker = lazy(() =>
  Promise.all([
    import("@emoji-mart/data"),
    import("@emoji-mart/react"),
  ]).then(([dataModule, { default: Picker }]) => ({
    default: (props) => <Picker data={dataModule.default} {...props} />,
  }))
);

function StepCreate({ form, setForm }) {
  // Hardcoded/local only for now, same as AgentCard's own emoji square
  // (AgentsTab.jsx: "Hardcoded emoji square, no functionality behind it
  // yet") — Rahim, 2026-09-02: "hardcode it for now". Not part of `form`,
  // so it isn't sent on save and resets if this step unmounts; wiring it
  // to a real per-agent icon field is a separate later task.
  const [icon, setIcon] = useState("🤖");
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", maxWidth: SHORT_FIELD_WIDTH }}>
        {/* Own "Emoji" label above the box, same as every other field here
            — not a hover tooltip (Rahim, 2026-09-02: "i asked you to add a
            placeholder of emoji above the emoji box", after the tooltip
            attempt missed what was actually being asked for). */}
        <LabeledField label="Emoji" sx={{ flexShrink: 0 }}>
          <Box
            onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
            sx={{
              width: 40, height: 40, flexShrink: 0,
              borderRadius: "8px",
              border: "1px solid", borderColor: "divider",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.25rem", lineHeight: 1, cursor: "pointer",
              boxSizing: "border-box",
              "&:hover": { borderColor: "text.secondary" },
            }}
          >
            {icon}
          </Box>
          <Popover
            open={Boolean(emojiAnchorEl)}
            anchorEl={emojiAnchorEl}
            onClose={() => setEmojiAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Suspense fallback={<Box sx={{ width: 320, height: 360 }} />}>
              <EmojiPicker
                onEmojiSelect={(emoji) => { setIcon(emoji.native); setEmojiAnchorEl(null); }}
                theme={theme.palette.mode}
              />
            </Suspense>
          </Popover>
        </LabeledField>
        <LabeledField label="Agent Name *" sx={{ flex: 1 }}>
          <TextField
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. Support Bot"
            fullWidth size="small"
            // small TextFields render at ~37px by default — bumped to 40px
            // so the box height matches the emoji square exactly (Rahim,
            // 2026-09-02: "make sure the box hight is same as the agent
            // name text box").
            sx={[inboxInputSx, { "& .MuiOutlinedInput-root": { minHeight: 40 } }]}
          />
        </LabeledField>
      </Box>
      <ExpandableParagraphField
        label="Description"
        sx={{ maxWidth: SHORT_FIELD_WIDTH }}
        minRows={3}
        placeholder="e.g. You are a helpful e-commerce assistant."
        value={form.instructions.role}
        onChange={(v) =>
          setForm((prev) => ({
            ...prev,
            instructions: { ...prev.instructions, role: v },
          }))
        }
      />
      {/* Font sizing pass (Rahim, 2026-09-02: "make the text font propelry
          match our new sizing and weight") — FormControlLabel's label
          defaults to the theme's bare body1 (0.75rem), same "too small"
          issue as the plain variant="body2"/"h6" Typography below. Passing
          a Typography node as `label` instead of a bare string is the fix.
          Each toggle is now a direct child of the outer gap:2.5 column
          (Rahim, 2026-09-02: "add standard space between the each field")
          — they used to share one un-gapped wrapper Box, so the two
          toggles sat right on top of each other while every other field
          had the full 20px gap. FormControlLabel's own negative margins
          (-11px left) still pull it slightly off from the fields above, so
          ml:0 neutralizes that instead of guessing a gap that compensates. */}
      {/* Rahim, 2026-09-03: "fix the text beside it as well ... weight
          correct and space between the text and the toggle" — PillSwitch
          is a tighter 40px box than the stock MUI Switch this label
          spacing was originally tuned against (2026-09-02), so the
          previous plain-string/no-weight label plus FormControlLabel's
          baked-in gap needed both an explicit weight and an explicit,
          deliberate gap instead of relying on defaults. Same
          pillSwitchLabelSx used on every PillSwitch label across this
          page (OfficeHoursSection.jsx/ResponseFormatSection.jsx too) for
          one consistent result. */}
      <FormControlLabel
        sx={pillSwitchLabelSx}
        control={
          <PillSwitch
            checked={form.isDefault}
            onChange={(e) => setForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
          />
        }
        label="Default agent for this workspace"
      />
      <FormControlLabel
        sx={pillSwitchLabelSx}
        control={
          <PillSwitch
            checked={form.isActive}
            onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
          />
        }
        label="Active"
      />
    </Box>
  );
}

// Fallback Message "Generate with AI" pool (Rahim, 2026-09-03: "no user
// want to figure out a fallback message" — a random pick from a hardcoded
// set, dressed up with a brief fake-thinking spinner so it *feels*
// AI-generated; genuinely no API call, no backend involved at all).
// Kept short and generic (Rahim, 2026-09-03: "use generic and better
// fallback messages") — no assumptions about tone/industry, works for any
// agent as a drop-in default rather than reading like one specific brand's
// voice.
const FALLBACK_MESSAGE_SUGGESTIONS = [
  "I'm not sure about that one, but a team member will follow up shortly. 🙋",
  "Great question — let me pass this to the team for a proper answer. 🙌",
  "That's outside what I can help with right now. Someone will get back to you soon. 👍",
  "I don't have enough information to answer that accurately just yet. 📩",
  "Let me get a team member to help you with this. 😊",
  "I want to make sure you get the right answer — the team will follow up shortly. ✅",
];

// Per-type glyph for a knowledge source, used both in the picker dialog
// list and on the selected pills (Rahim, 2026-09-03: "a preety pill
// displaying the selected doc with a icon") — same iconsax set as the rest
// of this page instead of a generic file icon for every row.
const SOURCE_TYPE_ICONS = {
  pdf: DocumentTextIconIx,
  docx: DocumentText1IconIx,
  txt: NoteTextIconIx,
  xlsx: TableDocumentIconIx,
  url: GlobalIconIx,
  faq: MessageQuestionIconIx,
};
// A distinct accent per source type so the selected pills read as
// colorful at a glance (Rahim, 2026-09-03: "fully rounded pill use button
// type pill with colourfull colours") — same palette family as the Tasks
// module's person-avatar/status colors, and the same soft-pill formula
// (getFieldOptionSoftPillSx: alpha bg + accent border + lighten/darken text,
// dark-mode aware) that already renders Tasks' colored Type/Status chips.
// txt was flat grey — not actually "colourful" (Rahim, 2026-09-03: "use
// yellow as a colour as well") — swapped for a real yellow so all six
// types read as distinct colors.
const SOURCE_TYPE_COLORS = {
  pdf: "#EF4444",
  docx: "#3B82F6",
  txt: "#EAB308",
  xlsx: "#10B981",
  url: "#8B5CF6",
  faq: "#F59E0B",
};
const sourcePillSx = (theme, type) => ({
  ...getFieldOptionSoftPillSx(theme, SOURCE_TYPE_COLORS[type] || SOURCE_TYPE_COLORS.txt),
  // 8px, not a fully-rounded 999px pill (Rahim, 2026-09-03: "dont use fully
  // rounded corners for the knowledge sources use less round as we made a
  // button") — matches neutralButtonSx's own borderRadius, this page's one
  // "button" radius convention, instead of the Tasks module's pill shape.
  borderRadius: "8px",
  fontWeight: 600,
  // Taller, more box-like — a plain small Chip read too thin (Rahim,
  // 2026-09-03: "increase the hight of the pill it should be more box
  // like"). Height bumped + padding opened up instead of the default
  // ~24px small-Chip strip.
  height: 34,
  fontSize: "0.8125rem",
  "& .MuiChip-label": { px: 1.1 },
  "& .MuiChip-icon": { color: "inherit", ml: 1 },
  // Small + tight, not MUI's default CancelIcon (a heavy filled circle-X
  // that reads "bad" at this pill size, Rahim 2026-09-03) — same small
  // CloseIcon-at-14px convention InboxAll.jsx's own label-filter chips use.
  "& .MuiChip-deleteIcon": { fontSize: "14px", color: "inherit", opacity: 0.6, ml: 0, mr: "6px", "&:hover": { opacity: 1 } },
});
function StepConfig({ form, setForm, sources, sourcesLoading, intentDraft, setIntentDraft, onAddIntent, onRemoveIntent }) {
  const fieldByKey = Object.fromEntries(INSTRUCTION_FIELDS.map((f) => [f.key, f]));
  // Knowledge-source picker — a centered Dialog (Rahim, 2026-09-03: the
  // Popover version "is very bad", pointed at Inbox's own "Filter by
  // Labels" Dialog as the reference: title+✕ header, checklist body,
  // Cancel/Apply footer — mirrored from InboxAll.jsx's filterOpen Dialog
  // below, same PaperProps/DialogTitle/DialogActions shape). Selection is
  // staged in `draftSourceIds` while the dialog is open so Cancel can
  // discard it — only Apply commits into `form.knowledgeSourceIds`.
  // Opened from either the empty-state "+ Add knowledge source" button or
  // the trailing "+" chip in the pill row, both feed the same dialog.
  const [sourcesDialogOpen, setSourcesDialogOpen] = useState(false);
  const [draftSourceIds, setDraftSourceIds] = useState([]);

  const updateInstruction = (key, value) =>
    setForm((prev) => ({ ...prev, instructions: { ...prev.instructions, [key]: value } }));

  const openSourcesDialog = () => {
    setDraftSourceIds(form.knowledgeSourceIds);
    setSourcesDialogOpen(true);
  };
  const toggleDraftSource = (id) =>
    setDraftSourceIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const applySourcesDialog = () => {
    setForm((prev) => ({ ...prev, knowledgeSourceIds: draftSourceIds }));
    setSourcesDialogOpen(false);
  };
  const removeSource = (id) =>
    setForm((prev) => ({ ...prev, knowledgeSourceIds: prev.knowledgeSourceIds.filter((x) => x !== id) }));

  // Fake "AI generate" for the Fallback Message field — genuinely just a
  // random pick from FALLBACK_MESSAGE_SUGGESTIONS, no API call. The brief
  // setTimeout is purely cosmetic (a spinner with zero delay reads as
  // broken/flickery, not "thinking").
  const [generatingFallback, setGeneratingFallback] = useState(false);
  const generateFallbackMessage = () => {
    setGeneratingFallback(true);
    setTimeout(() => {
      const pick = FALLBACK_MESSAGE_SUGGESTIONS[Math.floor(Math.random() * FALLBACK_MESSAGE_SUGGESTIONS.length)];
      setForm((prev) => ({ ...prev, fallbackMessage: pick }));
      setGeneratingFallback(false);
    }, 500);
  };

  // Paragraph-length fields (Context/Boundaries/Conversation Flow/Business
  // Rules) get their own full-width row; Communication Style/Tone are
  // one-liners so they share a row instead (Rahim, 2026-09-02: "context is
  // supposed to be a lengthy text so use a para type text box" — confirmed
  // side-by-side for the two short ones specifically).
  // minRows={2} forced every field (including the two short one-liners) to
  // a 2-line-tall box, so the placeholder sat anchored at the top with
  // empty space below instead of centered (Rahim, 2026-09-02: "the helper
  // text is nt in the center of the text box", confirmed from screenshot).
  // Communication Style/Tone are single-line answers — not multiline at
  // all now, so they center exactly like Agent Name's field. The 4
  // paragraph fields stay multiline (they genuinely need to wrap/grow).
  // minRows bumped from 1 to 4 (Rahim, 2026-09-03: "make the paragraph text
  // box height better so it is obvious that it will contain a big para" —
  // e.g. Context) — top-anchored placeholder is correct here, unlike the
  // minRows={2}-on-every-field regression above: these are genuine
  // paragraph boxes, not one-liners, so a Gmail/Slack-compose-style tall
  // box reads as intentional instead of broken.
  // Communication Style/Tone are one-line answers — plain TextField, no
  // expand/scroll machinery needed. Context/Boundaries/Conversation Flow/
  // Business Rules are the genuine "big paragraph" fields — all 4 now go
  // through ExpandableParagraphField (Rahim, 2026-09-04: first scoped to
  // Context only to try it, then "do it for all the paragraph text
  // boxes... all around the ai agents pages" once approved).
  const SHORT_INSTRUCTION_KEYS = ["communicationStyle", "tone"];
  const renderInstructionField = (key, extraSx) => {
    const field = fieldByKey[key];
    const isShort = SHORT_INSTRUCTION_KEYS.includes(key);
    if (!isShort) {
      return (
        <ExpandableParagraphField
          key={key}
          label={field.label}
          value={form.instructions[key]}
          onChange={(v) => updateInstruction(key, v)}
          placeholder={field.hint}
        />
      );
    }
    return (
      <LabeledField key={key} label={field.label} sx={extraSx}>
        <TextField
          value={form.instructions[key] || ""}
          onChange={(e) => updateInstruction(key, e.target.value)}
          fullWidth size="small" placeholder={field.hint}
          sx={inboxInputSx}
        />
      </LabeledField>
    );
  };

  return (
    // gap 3->2 (Rahim, 2026-09-03: "reduce the spacing for all the cards")
    // — 24px between every stacked SectionCard (Instructions, Knowledge
    // Sources, Fallback Message, Office Hours, Message Format, Intents)
    // read as excessive next to the just-tightened internal spacing.
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SectionCard heading="Instructions" description="Define how your agent should respond and communicate." icon={NoteIconIx}>
        {renderInstructionField("context")}

        <Box sx={{ display: "flex", gap: 2 }}>
          {renderInstructionField("communicationStyle", { flex: 1 })}
          {renderInstructionField("tone", { flex: 1 })}
        </Box>
        {renderInstructionField("boundaries")}
        {renderInstructionField("flow")}
        {renderInstructionField("businessRules")}
      </SectionCard>

      <SectionCard heading="Knowledge Sources" description="Attach sources the agent can pull accurate, on-brand answers from." icon={BookIconIx}>
        {form.knowledgeSourceIds.length === 0 ? (
          // Empty state — icon + heading + copy + CTA, same shape as the
          // competitor reference Rahim shared (2026-09-03), matching this
          // app's own icon-empty-state convention (Dashboard's
          // Contacts/TeamMembers/Lifecycle panels) rather than a plain
          // "None selected" dropdown.
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", py: 4 }}>
            {/* Rahim-supplied open-book icon, plain text.secondary — "no
                colour needed keep it simple" (2026-09-03), reverting the
                STEP_FILLED_COLOR accent tried earlier. */}
            <Box sx={{ mb: 1.5, color: "text.secondary", display: "flex" }}>
              <KnowledgeSourceIcon size={40} />
            </Box>
            {/* Rahim, 2026-09-03, screenshot: "still uses black colour for
                text ... Train your AI Agent" — confirmed live (computed
                color: rgb(0,0,0) in dark mode). MUI Typography's `color`
                prop defaults to "initial" (no color style applied at
                all), NOT theme-aware — it was relying on inheriting
                text.primary from an ancestor that, here, never actually
                set it, so it fell through to the browser's plain default
                black. Every other Typography on this page already sets
                color explicitly for exactly this reason; this was the
                one spot that didn't. */}
            <Typography variant="h6" sx={{ fontSize: "0.9375rem", color: "text.primary" }}>Train your AI Agent</Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5, maxWidth: 320 }}>
              To improve accuracy, attach company-specific documents and links from your knowledge library.
            </Typography>
            {/* Back to a simple button with a plain "+" (Rahim, 2026-09-03:
                "dont use coloured button use simple button with + icon") —
                the filled STEP_FILLED_COLOR treatment tried first was
                reverted; this matches neutralButtonSx, the bordered/neutral
                treatment every other button on this feature already uses. */}
            <Button
              variant="outlined"
              onClick={openSourcesDialog}
              startIcon={<AddIcon sx={{ fontSize: "1.05rem" }} />}
              sx={{ ...neutralButtonSx, mt: 2 }}
            >
              Add knowledge source
            </Button>
          </Box>
        ) : sourcesLoading ? (
          // Was falling straight into the populated branch below even
          // while `sources` hadn't loaded yet — `sources.find()` came back
          // undefined for every id, and `label={src?.title || id}` silently
          // fell back to printing the RAW MONGO ID as the chip text (Rahim,
          // 2026-09-04: found while checking "does the create/edit page use
          // our new skeleton"). An agent that already has sources attached
          // (the edit flow) would flash raw ObjectId strings until the
          // fetch resolved. Skeleton pills, same 34px/8px shape as the real
          // ones, instead of ever rendering that fallback.
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {form.knowledgeSourceIds.map((id) => (
              <Skeleton key={id} variant="rounded" width={120} height={34} sx={{ borderRadius: "8px" }} />
            ))}
          </Box>
        ) : (
          // Populated state — colorful pills (Rahim, 2026-09-03: "a preety
          // pill ... with a icon"), each with its own delete (✕) via
          // Chip's built-in onDelete instead of reopening a dropdown just
          // to remove one, plus a trailing "+" chip to add more — same
          // pattern this page's own Intents chips already use.
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
            {form.knowledgeSourceIds.map((id) => {
              const src = sources.find((s) => String(s._id) === id);
              const SourceIcon = SOURCE_TYPE_ICONS[src?.type] || DocumentTextIconIx;
              return (
                <Chip
                  key={id}
                  icon={<SourceIcon size={16} variant="Bold" />}
                  label={src?.title || id}
                  onDelete={() => removeSource(id)}
                  deleteIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                  sx={(theme) => sourcePillSx(theme, src?.type)}
                />
              );
            })}
            {/* Same 34px height as the pills above so it sits flush in the
                row instead of looking like a smaller, different control. */}
            <Chip
              variant="outlined"
              icon={<AddIcon sx={{ fontSize: 16 }} />}
              label="Add"
              onClick={openSourcesDialog}
              sx={{
                height: 34,
                borderRadius: "8px",
                borderStyle: "dashed",
                cursor: "pointer",
                color: "text.secondary",
                "& .MuiChip-label": { px: 1.1 },
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          </Box>
        )}

        {/* Picker dialog — Inbox's own "Filter by Labels" Dialog, mirrored
            structurally (title+✕ header / checklist body / Cancel+Apply
            footer, same PaperProps radius+shadow) per Rahim's direct
            reference (2026-09-03). One deliberate deviation: the Apply
            button stays this feature's neutralButtonSx bordered treatment,
            not InboxAll.jsx's primary.main (brand green) fill — this page
            has an explicit "never green button" rule (see neutralButtonSx
            above) that predates and overrides the copied reference. */}
        <Dialog
          open={sourcesDialogOpen}
          onClose={() => setSourcesDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow: (theme) =>
                theme.palette.mode === "dark" ? "0px 8px 24px rgba(0,0,0,0.4)" : "0px 8px 24px rgba(0,0,0,0.15)",
              padding: 0,
              maxHeight: "72vh",
              backgroundColor: "background.paper",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              fontWeight: 600, fontSize: "1.05rem",
              padding: "16px 20px", marginBottom: 0,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            Add Knowledge Sources
            <IconButton
              onClick={() => setSourcesDialogOpen(false)}
              size="small"
              aria-label="Close"
              sx={{
                color: "text.secondary",
                "&:hover": { backgroundColor: "action.hover", color: "text.primary" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 2.5, pb: 1.5 }}>
            <Box sx={{ pt: 2.5, ...slimScrollbarSx, maxHeight: 360, overflowY: "auto" }}>
              {sourcesLoading ? (
                // Was falling into the sources.length===0 branch below —
                // "No sources yet" printed regardless of whether that was
                // true or the fetch just hadn't resolved yet (Rahim,
                // 2026-09-04). Skeleton rows matching the real row shape
                // (checkbox + icon + title/subtitle) below instead.
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {[0, 1, 2].map((i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.1, px: 1.25 }}>
                      <Skeleton variant="rounded" width={18} height={18} sx={{ ...skeletonSx, borderRadius: "4px" }} />
                      <Skeleton variant="circular" width={18} height={18} sx={skeletonSx} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="55%" sx={{ ...skeletonSx, fontSize: "0.9375rem" }} />
                        <Skeleton variant="text" width="30%" sx={{ ...skeletonSx, fontSize: "0.75rem" }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : sources.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.secondary", opacity: 0.4 }}>
                    <KnowledgeSourceIcon size={40} />
                  </Box>
                  <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", fontWeight: 500 }}>
                    No sources yet
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.disabled", mt: 0.5 }}>
                    Add some in the Knowledge Sources tab.
                  </Typography>
                </Box>
              ) : (
                sources.map((src) => {
                  const id = String(src._id);
                  const checked = draftSourceIds.includes(id);
                  const SourceIcon = SOURCE_TYPE_ICONS[src.type] || DocumentTextIconIx;
                  return (
                    <ButtonBase
                      key={id}
                      onClick={() => toggleDraftSource(id)}
                      sx={{
                        width: "100%", py: 1.1, px: 1.25, mb: 0.5,
                        borderRadius: "8px", justifyContent: "flex-start", gap: 1,
                        fontSize: "0.9375rem", color: "text.primary", textAlign: "left",
                        "&:hover": { backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)") },
                      }}
                    >
                      <Checkbox checked={checked} size="small" sx={{ p: 0 }} />
                      {/* Neutral here, not colored — color is reserved for
                          the actual pills outside the dialog (Rahim,
                          2026-09-03: "dont use colour in the pop up it will
                          only show for the actual box"). */}
                      <Box sx={{ color: "text.secondary", opacity: 0.7, display: "flex", flexShrink: 0 }}>
                        <SourceIcon size={18} variant="Bold" />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{src.title}</Box>
                        <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{SOURCE_TYPE_LABELS[src.type] || src.type}</Box>
                      </Box>
                    </ButtonBase>
                  );
                })
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ padding: "14px 20px 18px", justifyContent: "space-between", borderTop: (theme) => `1px solid ${theme.palette.divider}` }}>
            <Button
              onClick={() => setSourcesDialogOpen(false)}
              sx={{ color: "text.secondary", textTransform: "none", fontSize: "0.8125rem", fontWeight: 500, borderRadius: "8px", px: 1.25 }}
            >
              Cancel
            </Button>
            <Button variant="outlined" onClick={applySourcesDialog} sx={neutralButtonSx}>
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </SectionCard>

      {/* Message Format moved up to sit BEFORE Fallback Message/Office
          Hours (Rahim, 2026-09-03: "instead of making format message the
          last container let's take it two steps up") — it was the very
          last section on the page, which is exactly why its 3 Selects
          kept running out of room below to open into (the maxHeight cap
          in inboxSelectMenuProps was fighting that from the other end).
          Two sections now sit below it instead of zero, so in the normal
          case there's real room in the viewport again; the maxHeight cap
          stays on as a fallback for when there genuinely isn't (a short
          window, or the user's already scrolled down).
          Extra padding on just this one card (Rahim, 2026-09-03: "make the
          whole card bigger") — Message Format has noticeably more content
          than the other cards (2 toggles + captions, 3 selects), so it
          gets its own bump past the shared SectionCard p:2 instead of
          changing that shared default for every other card too. */}
      <SectionCard sx={{ p: 3 }}>
        <ResponseFormatSection form={form} setForm={setForm} />
      </SectionCard>

      <SectionCard heading="Fallback Message" description="Shown when the agent doesn't have enough information to answer." icon={MessageQuestionIconIx}>
        <TextField
          value={form.fallbackMessage}
          onChange={(e) => setForm((prev) => ({ ...prev, fallbackMessage: e.target.value }))}
          fullWidth size="small" multiline minRows={1}
          sx={inboxInputSx}
          // "Generate with AI" (Rahim, 2026-09-03) — end-adornment button,
          // same AutoAwesomeIcon this page's own GenerateMenuPanel.jsx
          // already uses for its real AI-backed generate action, so this
          // reads as the same feature language even though it's just a
          // random pick from a hardcoded pool under the hood.
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {/* Labeled, not a bare icon (Rahim, 2026-09-03: "for the
                    icon use word generate") — reads as a clear action
                    instead of a mystery glyph. */}
                <Button
                  size="small"
                  onClick={generateFallbackMessage}
                  disabled={generatingFallback}
                  startIcon={generatingFallback ? <CircularProgress size={14} thickness={5} /> : <AutoAwesomeIcon sx={{ fontSize: "1rem" }} />}
                  sx={{ color: "text.secondary", textTransform: "none", fontSize: "0.75rem", fontWeight: 600, minWidth: "auto", px: 1 }}
                >
                  Generate
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </SectionCard>

      {/* No `heading` here — OfficeHoursSection/ResponseFormatSection already
          render their own internal section heading, so a second one from
          SectionCard would just duplicate it. */}
      <SectionCard>
        <OfficeHoursSection officeHours={form.officeHours} setForm={setForm} />
      </SectionCard>

      {/* Intents SectionCard hidden from the frontend (Rahim, 2026-09-03:
          "disable the intents section from showing on frontend like keep
          all the functionality and everything just that we wont show it
          any place in the frontend") — UI-only, deliberately NOT deleted.
          Left commented out (not removed) specifically so it's trivial to
          restore. Nothing else touched: form.intents, intentDraft/
          setIntentDraft, onAddIntent/onRemoveIntent are all still received
          as props here and still fully wired in AiSettings.jsx (still
          saved with the agent, still read by the backend's rule-based
          matcher before AI is called) — only this rendering block stopped.
      <SectionCard heading="Intents" description="Rule-based matching before AI is called." icon={FlashIconIx}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {form.intents.map((intent, i) => (
            <Chip
              key={`${intent.name}-${i}`}
              label={`${intent.name}${intent.entityKey ? ` → ${intent.entityKey}` : ""}: ${(intent.keywords || []).join(", ")}`}
              onDelete={() => onRemoveIntent(i)}
              size="small"
            />
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
          <LabeledField label="Intent name">
            <TextField
              value={intentDraft.name}
              onChange={(e) => setIntentDraft((prev) => ({ ...prev, name: e.target.value }))}
              size="small" sx={{ width: 160 }}
            />
          </LabeledField>
          <LabeledField label="Keywords (comma-separated)" sx={{ flex: 1, minWidth: 180 }}>
            <TextField
              value={intentDraft.keywords}
              onChange={(e) => setIntentDraft((prev) => ({ ...prev, keywords: e.target.value }))}
              size="small" fullWidth
            />
          </LabeledField>
          <LabeledField label="Entity key (optional)">
            <TextField
              value={intentDraft.entityKey}
              onChange={(e) => setIntentDraft((prev) => ({ ...prev, entityKey: e.target.value }))}
              size="small" sx={{ width: 140 }} placeholder="e.g. city"
            />
          </LabeledField>
        </Box>
        <Button onClick={onAddIntent} variant="outlined" size="small" sx={{ ...neutralButtonSx, alignSelf: "flex-start" }}>
          Add Intent
        </Button>
      </SectionCard>
      */}
    </Box>
  );
}

// Per-action-group glyph (Rahim, 2026-09-03: "add icons and sub para just
// like we did for the config") — same iconsax set as the rest of this
// page. button_message/catalog_send reuse the exact icons Allowed
// Formats' own pills already use for those same two concepts.
const ACTION_ICONS = {
  button_message: MessageTickIconIx,
  catalog_send: ShoppingBagIconIx,
  api_integration: CodeCircleIconIx,
  image_message: GalleryIconIx,
  assignment: ProfileAddIconIx,
  close_conversation: TickCircleIconIx,
};

function StepActions({ form, setForm, workspaceId }) {
  const isActionEnabled = (types) => types.every((t) => (form.enabledActions || []).includes(t));

  const toggleAction = (types) => {
    const allEnabled = types.every((t) => (form.enabledActions || []).includes(t));
    setForm((prev) => ({
      ...prev,
      enabledActions: allEnabled
        ? (prev.enabledActions || []).filter((t) => !types.includes(t))
        : [...new Set([...(prev.enabledActions || []), ...types])],
    }));
  };

  const updateConfig = (configKey, field, value) => {
    setForm((prev) => ({
      ...prev,
      actionConfigs: {
        ...(prev.actionConfigs || {}),
        [configKey]: { ...(prev.actionConfigs?.[configKey] || {}), [field]: value },
      },
    }));
  };

  return (
    // gap:2, matching StepConfig's own rhythm between stacked SectionCards
    // — this step was the one place still using its own bare accordion
    // list instead of that established convention (Rahim, 2026-09-03: "add
    // each section into its own card just like we did in config").
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SectionCard>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          <Box
            sx={{
              width: 32, height: 32, flexShrink: 0,
              borderRadius: "8px",
              border: "1px solid", borderColor: "divider",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "text.primary",
            }}
          >
            <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MagicStarIconIx size="100%" color="currentColor" variant="Linear" />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>AI Menu Builder</Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
              Generates ready-to-use menu items from your Instructions — review and Save Agent to keep them.
            </Typography>
          </Box>
        </Box>
        <GenerateMenuPanel form={form} setForm={setForm} workspaceId={workspaceId} />
      </SectionCard>

      {/* "Enable button message" hidden from the frontend (Rahim,
          2026-09-03: "do the same thing" as Intents — UI-only, keep all
          functionality) — ACTION_TOGGLE_GROUPS itself is untouched, so
          isActionEnabled/toggleAction/updateConfig, this agent's already-
          saved enabledActions/actionConfigs, and the backend's own use of
          button_message are all still fully wired; this filter only
          stops it from being one of the SectionCards rendered below.
          Restore by dropping this filter. */}
      {ACTION_TOGGLE_GROUPS.filter(({ configKey }) => configKey !== "button_message").map(({ label, description, types, configKey }) => {
        const enabled = isActionEnabled(types);
        const Icon = ACTION_ICONS[configKey];
        return (
          <SectionCard key={configKey}>
            {/* Same icon/heading/description/toggle shape as Office
                Hours/Message Format — toggle aligns to the description
                line, not the heading. */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              <Box
                sx={{
                  width: 32, height: 32, flexShrink: 0,
                  borderRadius: "8px",
                  border: "1px solid", borderColor: "divider",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "text.primary",
                }}
              >
                <Box sx={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size="100%" color="currentColor" variant="Linear" />
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "text.primary" }}>{label}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.25 }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>{description}</Typography>
                  <PillSwitch checked={enabled} onChange={() => toggleAction(types)} />
                </Box>
              </Box>
            </Box>
            {/* Fields just appear plainly inside the same card now (Rahim,
                2026-09-03: "no need for that green line or changing
                background colour") — the left accent border + action.hover
                fill this used to get on expand is gone; mt:2 is the only
                separation from the heading row above. */}
            <Collapse in={enabled} unmountOnExit>
              <Box sx={{ mt: 2 }}>
                <ActionConfigPanel
                  configKey={configKey}
                  actionConfigs={form.actionConfigs}
                  updateConfig={updateConfig}
                />
              </Box>
            </Collapse>
          </SectionCard>
        );
      })}
    </Box>
  );
}

// Wrapped in the same SectionCard every other step's sections use (Rahim,
// 2026-09-04: "the only page left is this ... enhance the font sizing and
// everything just like we did for the other pages") — this was the one
// remaining step still rendering its own bare heading instead of going
// through the shared icon+heading+description card shape.
function StepContactFilter({ form, setForm }) {
  return (
    <SectionCard heading="Contact Filter" description="Choose which contacts this agent will handle." icon={FilterIconIx}>
      <ContactFilterSection contactFilter={form.contactFilter} setForm={setForm} />
    </SectionCard>
  );
}

export default function AgentConfigView({
  // onSave/saving dropped (Rahim, 2026-09-04: Save Agent moved up into
  // AiSettings.jsx's PanelHeading, which now owns that action directly —
  // this view no longer renders a Save Agent button of its own at all).
  form, setForm, onBack, sources, sourcesLoading,
  workspaceId, editingAgentId,
  intentDraft, setIntentDraft,
  onAddIntent, onRemoveIntent,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [testMessages, setTestMessages] = useState([]);
  const [testInput, setTestInput] = useState("");
  const [testing, setTesting] = useState(false);

  // Reset Chat (Rahim, 2026-09-04, matching the reference screenshot) —
  // genuinely simple to make real rather than decorative: testMessages/
  // testInput are local component state, no server-side session to clear.
  // sendTestMessage always sends `history: testMessages` fresh from this
  // state each call, so clearing it here is a real, complete reset.
  const resetTestChat = () => {
    setTestMessages([]);
    setTestInput("");
  };

  // Same emoji-picker open/close + outside-click-to-close mechanism as
  // Inbox's own TextBox.jsx (see the EmojiPicker lazy import above).
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerOpen &&
        emojiPickerRef.current && !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current && !emojiButtonRef.current.contains(event.target)
      ) {
        setEmojiPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [emojiPickerOpen]);

  const sendTestMessage = async () => {
    if (!testInput.trim() || !editingAgentId) return;
    const content = testInput.trim();
    const history = testMessages;
    setTestMessages((prev) => [...prev, { role: "user", content }]);
    setTestInput("");
    setTesting(true);
    try {
      const token = await getAuthToken();
      const res = await fetch(
        `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents/${editingAgentId}/test`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ message: content, history }),
        }
      );
      const data = await res.json();
      setTestMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply || "No reply received." },
      ]);
    } catch {
      setTestMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Test failed. Check server connection." },
      ]);
    } finally { setTesting(false); }
  };

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    // gap:3 (24px) matches the page's own left/right padding (Rahim,
    // 2026-09-02: "the space between the test agent and next button is not
    // same as the left and right padding space of the page") — was gap:2
    // (16px), an unrelated mismatch left over from before that padding fix.
    <Box sx={{ display: "flex", gap: 3, height: "100%", alignItems: "stretch" }}>
      {/* Left — header + stepper + scrollable config + sticky nav. */}
      <Box
        sx={{
          // flex-grow based, not a fixed "0 0 70%" basis — a non-shrinking
          // fixed-percent pair here (70% + 30%) doesn't leave room for the
          // row's own `gap`, so the two panels together overflowed past the
          // page's right padding by about that gap width (Rahim,
          // 2026-09-02: "padding right to the test agent panel is too
          // low" — confirmed via computed rects: 24px on the left, only
          // 6px on the right). Grow/shrink flex divides the space AFTER
          // the gap is subtracted, so both edges land flush again.
          flex: "7 1 0%",
          minWidth: 0,
          height: "100%",
          display: "flex", flexDirection: "column", gap: 2,
          overflow: "hidden",
          // Compensates for AiSettings.jsx dropping its top+bottom padding
          // (moved here so the right zone can touch this row's true top
          // AND bottom edges) — this column's own content still sits
          // exactly where it always has.
          pt: 3, pb: 3,
          // ⚠️ Same content-box bug as the zone/card fix — this Box
          // defaults to box-sizing:content-box, so the pt/pb above got
          // added ON TOP of height:"100%" (48px too tall), pushing the
          // Previous/Next footer row (flexShrink:0, last child) past the
          // visible bounds entirely since this Box also has
          // overflow:"hidden". border-box fixes it the same way.
          boxSizing: "border-box",
        }}
      >
        {/* Standalone "Save Agent" header row removed (Rahim, 2026-09-03:
            the Create/Config/Actions/Contact Filter stepper plus this row
            plus the footer were together eating too much viewport height
            before any real content showed). It was a full row of its own
            above the stepper, calling the exact same onSave/saving props
            as the footer's own Save Agent button — pure duplication, safe
            to delete (create vs edit is decided entirely by the parent's
            onSave via editingAgentId, not by which button called it).
            Save Agent now lives only in the footer below, and — this
            part matters — on EVERY step there, not just the last one
            (Rahim: "what if he made the agent was on page 3 and he had to
            go somewhere, he can just save and complete it after") —
            deleting this row without that change would have made
            mid-wizard saving impossible, a real regression this avoids.
            `onBack` is still passed in from the parent (unused here now).
            See [[ai-settings-page-rebuild-plan]].
            The inline error Alert that used to render here is gone —
            Save/validation errors now go through the app's real toast
            system (react-hot-toast, top-right under the nav) via
            AiSettings.jsx's handleSave, same as every other module
            (Rahim, 2026-09-04 audit: "the notifications are not in the
            correct place"). */}

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            flexShrink: 0,
            // MUI hardcodes the alternativeLabel connector's vertical
            // position at top:12px, i.e. the center of the default 24px
            // round StepIcon. This connector-root's "top" is independent
            // of the StepButton padding below in one specific sense: a
            // SYMMETRIC padding change (top+bottom equally) doesn't move
            // it at all, since the icon's own center stays put when both
            // sides grow together — confirmed live at 8px and 16px
            // symmetric, top:0 stayed correct both times. An ASYMMETRIC
            // change does shift it though (top-only bump to 26px moved
            // the icon center down ~6px, confirmed via
            // getBoundingClientRect) — re-verify with that method any
            // time this padding stops being symmetric, or the icon size
            // changes again.
            "& .MuiStepConnector-alternativeLabel": { top: 20 },
            // Default MUI connector line follows primary.main (brand
            // green) once a step is active/completed — swapped to the same
            // STEP_FILLED_COLOR mid-grey as the step circles above, divider
            // grey ahead.
            "& .MuiStepConnector-line": { borderColor: "divider" },
            "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": {
              borderColor: STEP_FILLED_COLOR,
            },
            "& .MuiStepLabel-label": { fontSize: "0.875rem", fontWeight: 600 },
            // StepButton/StepLabel both carry their own default vertical
            // padding independent of the icon size — zeroing it fully out
            // (an earlier pass) looked too cramped and, combined with a
            // stale connector "top" value from before the icon shrink,
            // briefly made the connector line render off-center too.
            // Bumped up again (Rahim, 2026-09-03: "add a little more top
            // and bottom padding" then "more top padding bottom is ok") —
            // top asymmetric now, bottom stayed at the previous value.
            "& .MuiStepButton-root": { paddingTop: "26px", paddingBottom: "16px" },
            "& .MuiStepLabel-iconContainer": { paddingRight: 0 },
            "& .MuiStepLabel-labelContainer .MuiStepLabel-label": { marginTop: "4px" },
          }}
        >
          {STEPS.map((label, index) => (
            <Step key={label}>
              {/* StepButton doesn't forward StepIconComponent to its inner
                  StepLabel (confirmed: it leaked straight onto the DOM
                  button and threw a React unknown-prop warning) — passing
                  a pre-rendered icon node via `icon` instead, computing
                  active/completed ourselves from activeStep since that's
                  the same linear-wizard logic MUI's own icon would use. */}
              <StepButton
                onClick={() => setActiveStep(index)}
                icon={<WizardStepIcon active={index === activeStep} completed={index < activeStep} icon={index + 1} />}
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>

        {/* pt gives the step content room to breathe below the stepper row
            (Rahim, 2026-09-02: "give top padding so it has proper gap
            between the above steps") — the stepper's own alternativeLabel
            layout has no bottom margin of its own. */}
        {/* slimScrollbarSx — same shared scrollbar every Inbox panel uses
            (Rahim, 2026-09-02: "change the scroll bar to mathc the one we
            used in inbox"), not the browser's unstyled default. */}
        <Box sx={{ flex: 1, overflowY: "auto", pr: 1, pt: 2, ...slimScrollbarSx }}>
          {activeStep === 0 && (
            <StepCreate form={form} setForm={setForm} />
          )}
          {activeStep === 1 && (
            <StepConfig
              form={form} setForm={setForm} sources={sources} sourcesLoading={sourcesLoading}
              intentDraft={intentDraft} setIntentDraft={setIntentDraft}
              onAddIntent={onAddIntent} onRemoveIntent={onRemoveIntent}
            />
          )}
          {activeStep === 2 && (
            <StepActions form={form} setForm={setForm} workspaceId={workspaceId} />
          )}
          {activeStep === 3 && (
            <StepContactFilter form={form} setForm={setForm} />
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", pt: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            sx={neutralButtonSx}
            disabled={activeStep === 0}
            onClick={() => setActiveStep((s) => s - 1)}
            startIcon={<ArrowBackIcon />}
          >
            Previous
          </Button>
          {/* Save Agent moved again (Rahim, 2026-09-04: "add the save
              agent and cancel agent button on the top nav right side ...
              go for it") — up into AiSettings.jsx's PanelHeading action
              slot now, which (like this footer) is visible on every step,
              so "save mid-wizard" still works without a footer copy too.
              Next stays here — it's genuinely step-local, unlike Save. */}
          {!isLastStep && (
            <Button
              variant="outlined"
              sx={neutralButtonSx}
              onClick={() => setActiveStep((s) => s + 1)}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Right — test chat. Back to always-on across every step (Rahim,
          2026-09-02: "get the text panel back ... get it back to always
          on" — reverting the step-4-only change from earlier).

          Two-layer split (Rahim, 2026-09-03, after correcting a first
          attempt that recolored the card itself: "its not the change the
          colour of the test agent container its changing the background
          of the whole section ... then we added our card on the [right]
          side which had different colour"): an outer ZONE fills the whole
          column with its own flat, distinct color (no border/radius — not
          a card), and the actual Test Agent CARD (unchanged border/
          radius/background.paper) floats inset inside it via padding, so
          the zone's color shows as a visible gutter around the card. Left
          column gets no equivalent zone — confirmed unchanged. */}
      <Box
        sx={{
          flex: "3 1 0%", minWidth: 0,
          // Plain height:"100%" now (the earlier mt:-3 + calc(100% + 24px)
          // bleed trick is gone — AiSettings.jsx's wrapper carries pt:0
          // instead, so this zone just naturally starts flush at the
          // row's true top with no hack needed here).
          height: "100%",
          // ⚠️ Real root cause of the missing-bottom-padding bug, found via
          // live computed styles (Rahim, 2026-09-03): this Box defaults to
          // `box-sizing: content-box` (MUI's Box doesn't force border-box),
          // so `height:"100%"` sized the CONTENT box to match the parent,
          // then `p` got added ON TOP of that for the rendered/border box
          // — e.g. p:3 (24px top+bottom = 48px) made the real height
          // 849(100%) + 48 = 897px, 48px taller than intended, pushing the
          // bottom past the row's own bottom (and past the viewport) with
          // nothing left for bottom padding. border-box is the fix: with
          // it, padding is subtracted FROM the 100%, not added on top.
          // Bumped 16px -> 24px uniform gutter (Rahim, 2026-09-03: "add
          // more spacing on all sides" / "decrease height" — since the
          // card fills 100% of this padded area, a bigger gutter here is
          // both asks at once: more visible zone-color margin AND a
          // shorter/narrower card, no separate height percentage needed.
          boxSizing: "border-box",
          p: 3,
          // Reverted the light-mode "try white" experiment (Rahim,
          // 2026-09-03: "keep the background as it was before" — the
          // "make it white" ask was actually about the card's own header
          // strip below, not this zone). Back to the cool-toned grey.
          // Dark mode's own hardcoded #1C2128 swapped for background.paper
          // (Rahim, 2026-09-04: "not the black colour card for dark mood
          // ... make it grey as in the colour of the heres how to get
          // started background colour for dark mood") — that's the exact
          // same real theme token (#1E1E1E) AgentsGetStartedPanel.jsx's
          // own dark-mode background already uses, instead of a one-off
          // hex guess for this zone. Light mode untouched — only the dark
          // one was called out.
          bgcolor: (theme) => (theme.palette.mode === "dark" ? theme.palette.background.paper : "#E9EDF2"),
          // The "disclaimer outside the card, as a second child of this
          // zone" detour (2026-09-04) is reverted — Rahim: "forget abt it
          // ... use that text instead" of the empty-state description
          // instead, and "make the card hight back to what it was before".
          // Back to a single child filling the zone, no column-flex needed.
        }}
      >
      <Box
        sx={{
          // Back to filling the zone's full (padded) height (Rahim,
          // 2026-09-03: "increase the height of the card properly with a
          // little space padding gap on all four side") — the earlier
          // height:"85%"-plus-centering pass shrank the card and centered
          // it inside the zone; that's dropped now in favor of the card
          // simply filling 100% of the space left after the zone's own
          // uniform p:2 padding, so the "little gap" is exactly that
          // padding on every side, not empty space above/below a smaller
          // card. Briefly flex:1 (2026-09-04, to share the zone's column
          // with a disclaimer sibling) — reverted back to height:"100%"
          // now that the disclaimer moved into the empty-state text
          // instead (Rahim: "make the card hight back to what it was
          // before").
          height: "100%", minHeight: 0,
          // Same content-box fix as the zone above — this Box's 1px border
          // was also getting added on top of height:"100%" instead of
          // being absorbed into it (2px total, minor on its own, but
          // correct for the same reason).
          boxSizing: "border-box",
          display: "flex", flexDirection: "column",
          // 16px (the AgentsTab card radius) read as too rounded for a
          // panel this narrow (Rahim, 2026-09-02: "a little too much
          // corner raius make it match the button ones") — dropped to 8px,
          // the same radius neutralButtonSx uses on every button here.
          border: "1px solid", borderColor: "divider", borderRadius: "8px",
          overflow: "hidden",
          // background.paper (white in light mode) read as its own
          // distinct white card next to the rest of the app's actual grey
          // (Rahim, 2026-09-03: "make the test agent card same colour as
          // the whole app ... change the white card to grey one").
          // background.default is that real page-background grey/near-
          // black (#f5f5f5 light / #121212 dark) — same token every other
          // page's own base background already uses, not background.paper
          // (the "elevated card" token SectionCard/AgentCard use instead).
          bgcolor: "background.default",
          // Elevation shadow tried in the previous pass, dropped (Rahim,
          // 2026-09-03: "no need for background shadow") — border alone
          // marks the card's edge now, same as every other bordered
          // surface on this page (SectionCard, AgentCard).
        }}
      >
        {/* Header strip: action.hover -> background.paper -> background.default
            (Rahim, 2026-09-03, most recently: "make the test agent card
            same colour as the whole app") — follows the card body's own
            bgcolor change above so the two keep matching instead of the
            header reading as a separate band again; the borderBottom
            divider still marks where the header ends. */}
        <Box sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.default", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Bordered-square icon treatment, same as Inbox's ChatInfoRail
              icon buttons (Rahim, 2026-09-02: "it should be in a box lie we
              did for the inbox page right rail") — that rail uses 40x40/
              10px radius for full rail buttons; scaled down here since
              this is an inline heading icon, not a clickable rail item. */}
          <Box
            sx={{
              width: 24, height: 24, flexShrink: 0,
              borderRadius: "6px",
              border: "1px solid", borderColor: "divider",
              display: "flex", alignItems: "center", justifyContent: "center",
              // Same missing-color bug as the Typography fixes above — the
              // icon's stroke="currentColor" was inheriting the page body's
              // raw black instead of the theme (Rahim, 2026-09-02: "the icon
              // of test agent its still black for dark mood").
              color: "text.primary",
            }}
          >
            <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TestAgentIcon size="100%" />
            </Box>
          </Box>
          {/* fontWeight 600 read too bold next to a plain icon (Rahim,
              2026-09-02: "test agent heading is too bold reduce bold as
              well") — dropped to 500, still distinct from the 0.8125rem
              body text below it. */}
          <Typography sx={{ fontSize: "1rem", fontWeight: 500, color: "text.primary" }}>Test Agent</Typography>
          </Box>
          {/* Reset Chat (Rahim, 2026-09-04, matching the reference
              screenshot's own header action) — real, not decorative: clears
              testMessages/testInput via resetTestChat above. Only shown
              once there's actually something to reset. */}
          {editingAgentId && testMessages.length > 0 && (
            <ButtonBase
              onClick={resetTestChat}
              sx={{
                fontSize: "0.75rem", fontWeight: 600,
                color: "text.secondary",
                px: 0.75, py: 0.25, borderRadius: "6px",
                "&:hover": { color: "text.primary", bgcolor: "action.hover" },
              }}
            >
              Reset Chat
            </ButtonBase>
          )}
        </Box>

        {!editingAgentId ? (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5, p: 3 }}>
            {/* text.disabled (MUI's own "empty/inactive state" token) reads
                more muted-grey than text.secondary (Rahim, 2026-09-02:
                "make the icon and text grey more") — same token on both so
                they still match each other. */}
            <Box sx={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", color: "text.disabled" }}>
              <SaveAgentFirstIcon size="100%" />
            </Box>
            <Typography sx={{ fontSize: "0.8125rem", color: "text.disabled", textAlign: "center" }}>
              Save the agent first to enable testing.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                flex: 1, overflowY: "auto", p: 1.5,
                display: "flex", flexDirection: "column",
                // Empty state centers in the available space instead of
                // sitting pinned to the bottom like a real message would
                // (Rahim, 2026-09-04, matching the reference screenshot's
                // own centered "Test your AI Agent" empty state).
                justifyContent: testMessages.length === 0 ? "center" : "flex-end",
                gap: 1,
                ...slimScrollbarSx,
              }}
            >
              {testMessages.length === 0 && (
                <Box sx={{ textAlign: "center", px: 2 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "text.primary" }}>
                    Test your AI Agent
                  </Typography>
                  {/* Swapped for the disclaimer text (Rahim, 2026-09-04:
                      "forget abt it just use that text instead of this")
                      — replaces the "send a message to preview" copy
                      instead of the disclaimer living as a separate
                      element outside the card. */}
                  <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5, lineHeight: 1.5 }}>
                    Actions taken here are for testing purposes only and won't affect your live agent.
                  </Typography>
                </Box>
              )}
              {testMessages.map((msg, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                    bgcolor: msg.role === "user" ? "primary.main" : "action.selected",
                    color: msg.role === "user" ? "primary.contrastText" : "text.primary",
                    borderRadius: 2, px: 1.5, py: 0.75,
                  }}
                >
                  <Typography sx={{ fontSize: "0.8125rem" }}>{msg.content}</Typography>
                </Box>
              ))}
              {testing && (
                <Box sx={{ alignSelf: "flex-start", bgcolor: "action.hover", borderRadius: 2, px: 1.5, py: 0.75 }}>
                  <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>Thinking…</Typography>
                </Box>
              )}
            </Box>
            {/* position:relative — the emoji picker below anchors off this
                Box (bottom:"100%"), same recipe as TextBox.jsx's real
                compose box. */}
            <Box sx={{ position: "relative" }}>
              {emojiPickerOpen && (
                <Box
                  ref={emojiPickerRef}
                  sx={{
                    position: "absolute", bottom: "100%", mb: 1, left: 8,
                    overflow: "hidden", borderRadius: "8px",
                    boxShadow: (t) => (t.palette.mode === "dark" ? "0px 4px 10px rgba(0,0,0,0.3)" : "0px 4px 10px rgba(0,0,0,0.1)"),
                    zIndex: (t) => t.zIndex.modal,
                  }}
                >
                  <Suspense fallback={null}>
                    <EmojiPicker
                      onEmojiSelect={(emoji) => setTestInput((prev) => prev + emoji.native)}
                      theme={theme.palette.mode}
                      previewPosition="none"
                    />
                  </Suspense>
                </Box>
              )}
              <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 1, borderTop: "1px solid", borderColor: "divider" }}>
                {/* Emoji button (Rahim, 2026-09-04, matching the reference
                    screenshot: "instead of adding file button we can give
                    emoji button") — a file-attach button here would've been
                    either fake or needed real upload plumbing just for a
                    test panel; this is genuinely functional and reuses the
                    exact picker Inbox's own compose box already has. */}
                <IconButton
                  ref={emojiButtonRef}
                  onClick={() => setEmojiPickerOpen((v) => !v)}
                  disabled={testing}
                  size="small"
                  sx={{ color: (t) => (emojiPickerOpen ? t.palette.text.primary : t.palette.text.secondary) }}
                >
                  <EmojiHappyIconIx size={18} variant="Linear" />
                </IconButton>
                <TextField
                  size="small" fullWidth placeholder="Type a test message…"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTestMessage(); }
                  }}
                  disabled={testing}
                  sx={inboxInputSx}
                />
                <IconButton
                  sx={{ color: (theme) => theme.palette.text.primary }}
                  onClick={sendTestMessage}
                  disabled={testing || !testInput.trim()}
                  size="small"
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </>
        )}
      </Box>
      </Box>
    </Box>
  );
}
